import { useEffect, useMemo, useState } from "react";
import { onSnapshot, query, where } from "firebase/firestore";
import useAuth from "hooks/useAuth";
import { whatsappNumberAccessCollection } from "utils/whatsapp/collections";
import { createAccountLiveStore, persistedCacheFor, useAccountLiveStore } from "utils/accountLiveStore";
import type { WhatsappNumberAccess } from "../@types/sharedSchema";

export type WhatsappNumberRow = WhatsappNumberAccess & { id: string };

/** Broadcasts a number switch to every mounted copy of this hook in THIS tab. */
const NUMBER_SELECTION_EVENT = "webot:whatsappNumberSelected";

const NO_NUMBERS: WhatsappNumberRow[] = [];

/**
 * ONE listener for the whole session, not one per mounted copy of this hook — the hub layout,
 * the page inside it and a dozen dialogs all read the same few docs. Remembered across a reload,
 * so the WhatsApp pages draw from the last known list instead of waiting a round trip each time
 * they open. See `accountLiveStore.ts`.
 */
const numbersStore = createAccountLiveStore<WhatsappNumberRow[]>({
  label: "whatsapp-number-access",
  listen: (email, onValue, onError) =>
    onSnapshot(
      query(whatsappNumberAccessCollection, where("memberMails", "array-contains", email)),
      (snap) => onValue(snap.docs.map((d) => d.data() as WhatsappNumberRow)),
      onError
    ),
  persistence: persistedCacheFor<WhatsappNumberRow[]>("webot:whatsappNumbers:v1")
});

const readStoredSelection = (storageKey: string | undefined): string | undefined => {
  if (!storageKey) return undefined;
  try {
    return localStorage.getItem(storageKey) ?? undefined;
  } catch {
    return undefined;
  }
};

/** The stored choice if it is still accessible, else the user's OWN number, else the first. */
const resolveSelectedKey = (
  numbers: WhatsappNumberRow[],
  stored: string | undefined,
  email: string | undefined
): string | undefined => {
  if (!numbers.length) return undefined;
  if (stored && numbers.some((n) => n.numberKey === stored)) return stored;
  const own = numbers.find((n) => n.ownerMail === email);
  return (own ?? numbers[0]).numberKey;
};

/**
 * The user's WhatsApp numbers (every `whatsapp-number-access` doc listing them as a member) plus
 * the "current active number" selection driving the WhatsApp pages.
 *
 * Selection persists per user in localStorage. Default: the user's OWN number when one exists
 * (that's the one they connected and expect to see first), else the first shared/system number.
 *
 * Keyed on the EMAIL — the access docs and firestore.rules use User.id (== email); useAuth's
 * `user.id` is the Firebase UID, which reads nothing and gets denied (the exact mix-up that
 * shipped on the messages page, 2026-07-30).
 */
export default function useWhatsappNumbers() {
  const { user } = useAuth();
  const email = user?.email ?? undefined;

  const { value, settled } = useAccountLiveStore(numbersStore, email ?? null);
  const numbers = value ?? NO_NUMBERS;
  const loading = !settled;

  const storageKey = email ? `webot:whatsappActiveNumber:${email}` : undefined;

  // Resolved on the FIRST render too: with a remembered list there is no frame in which the
  // numbers are known and nothing is selected.
  const [selectedKey, setSelectedKey] = useState<string | undefined>(() =>
    resolveSelectedKey(numbers, readStoredSelection(storageKey), email)
  );

  // Re-resolve whenever the list changes: stored choice if still accessible, else default.
  useEffect(() => {
    setSelectedKey(resolveSelectedKey(numbers, readStoredSelection(storageKey), email));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers.map((n) => n.numberKey).join("|"), email]);

  /**
   * Keeps every mounted copy of this hook on the same number.
   *
   * The hook is called independently by each page — and, since the smart-assistant tab, by the
   * hub LAYOUT too, which is mounted at the same time as the page inside it. Without this the two
   * hold separate `selectedKey` state: the switcher on the page would move, write localStorage
   * and re-render the page, while the tab strip above it kept rendering the previous number's
   * tabs until something remounted it. A `storage` event would not help — the browser fires that
   * only in OTHER tabs.
   */
  useEffect(() => {
    const onSelectionChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ email?: string; numberKey: string }>).detail;
      if (!detail || detail.email !== email) return;
      setSelectedKey(detail.numberKey);
    };
    window.addEventListener(NUMBER_SELECTION_EVENT, onSelectionChanged);
    return () => window.removeEventListener(NUMBER_SELECTION_EVENT, onSelectionChanged);
  }, [email]);

  const selectNumber = (numberKey: string) => {
    setSelectedKey(numberKey);
    if (storageKey) localStorage.setItem(storageKey, numberKey);
    window.dispatchEvent(new CustomEvent(NUMBER_SELECTION_EVENT, { detail: { email, numberKey } }));
  };

  const selected = useMemo(
    () => numbers.find((n) => n.numberKey === selectedKey),
    [numbers, selectedKey]
  );

  return { numbers, selected, selectedKey, selectNumber, loading };
}
